import React, { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, any>;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  keywords,
  ogType = 'website',
  ogImage = 'https://kheldrop.com/logo512.png',
  jsonLd,
}) => {
  useEffect(() => {
    // 1. Update Title
    const formattedTitle = title
      ? `${title} | KhelDrop`
      : 'KhelDrop | Geo-Spatial Arcade & Zero-Cost Game Network';
    document.title = formattedTitle;

    // Helper to update or create meta tags
    const setMetaTag = (attribute: string, name: string, content: string) => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // 2. Meta Description
    if (description) {
      setMetaTag('name', 'description', description);
      setMetaTag('property', 'og:description', description);
      setMetaTag('name', 'twitter:description', description);
    }

    // 3. Meta Keywords
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }

    // 4. OpenGraph & Twitter Titles
    setMetaTag('property', 'og:title', formattedTitle);
    setMetaTag('name', 'twitter:title', formattedTitle);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('name', 'twitter:image', ogImage);

    // 5. Canonical URL
    const fullCanonical = canonical
      ? (canonical.startsWith('http') ? canonical : `https://kheldrop.com${canonical}`)
      : window.location.origin + window.location.pathname;

    setMetaTag('property', 'og:url', fullCanonical);

    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = fullCanonical;

    // 6. Dynamic JSON-LD (if specified)
    let dynamicScript: HTMLScriptElement | null = null;
    if (jsonLd) {
      dynamicScript = document.createElement('script');
      dynamicScript.type = 'application/ld+json';
      dynamicScript.id = 'page-dynamic-jsonld';
      dynamicScript.text = JSON.stringify(jsonLd);
      document.head.appendChild(dynamicScript);
    }

    return () => {
      if (dynamicScript && dynamicScript.parentNode) {
        dynamicScript.parentNode.removeChild(dynamicScript);
      }
    };
  }, [title, description, canonical, keywords, ogType, ogImage, jsonLd]);

  return null;
};

export default SEOHead;
